import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { AuditPlanDetailDTO } from "@/modules/internal-audit/domain/contracts";
import type { AuditTypeDTO } from "@/modules/internal-audit/domain/contracts/type";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import { formatDateForInput } from "@/shared/lib/date-time";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  confirm,
  Field,
  FormInput,
  FormRoot,
  FormTextarea,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, Settings, SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  useAuditTypes,
  useCreateAuditType,
  useDeleteAuditType,
  useUpdateAuditType,
} from "../api/queries";
import { auditPlanFormSamples } from "./auditPlanFormSamples";
import { AuditTypeForm } from "./AuditTypeForm";

type DepartmentItem = LookupItem;
type PositionItem = LookupItem;
type AuditTypeItem = LookupItem;
type SOPItem = LookupItem;
type QCStandardItem = LookupItem;
type ChecklistItem = LookupItem;
type KPIItem = LookupItem;

const AuditPlanFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ຜະແນການກວດສອບ"),
  description: z.string().optional(),
  auditTypeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດການກວດສອບ"),
  sopId: z.string().optional(),
  qcStandardId: z.string().optional(),
  checklistId: z.string().optional(),
  kpiId: z.string().optional(),
  departmentId: z.string().min(1, "ກະລຸນາເລືອກພະແນກ"),
  auditorPositionId: z.string().min(1, "ກະລຸນາເລືອກຕໍາແໜ່ງຜູ້ກວດສອບ"),
  frequency: z.enum([
    "monthly",
    "quarterly",
    "semi_annual",
    "annual",
    "ad_hoc",
  ]),
  nextAuditDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີກວດສອບຄັ້ງຖັດ"),
  status: z.enum(["pending", "in_progress", "completed", "cancelled"]),
  isActive: z.boolean().default(true),
});

export type AuditPlanFormValues = z.infer<typeof AuditPlanFormSchema>;

export type AuditPlanFormRef = {
  getValues: () => AuditPlanFormValues | undefined;
  setValue: <T extends keyof AuditPlanFormValues>(
    name: T,
    value: AuditPlanFormValues[T],
  ) => void;
};

type AuditPlanFormProps = {
  initialData?: AuditPlanDetailDTO;
  onSubmit: (data: AuditPlanFormValues) => void;
  isLoading?: boolean;
  formRef?: React.RefObject<AuditPlanFormRef | null>;
};

// Type Management Component
function TypeManagementButton() {
  const canManage = useActionPermission([
    "internal_audit:create",
    "internal_audit:update",
    "internal_audit:delete",
  ]);
  const listModal = useDisclosure();
  const createModal = useDisclosure();
  const editModal = useDisclosure<AuditTypeDTO>();
  const listQuery = useAuditTypes({
    limit: 100,
    offset: 0,
    sort: undefined,
    filters: undefined,
  });
  const createType = useCreateAuditType();
  const updateType = useUpdateAuditType();
  const deleteType = useDeleteAuditType();

  if (!canManage) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => listModal.open()}
        title="ຈັດການປະເພດ"
        className="mt-6"
      >
        <Settings className="h-4 w-4" />
      </Button>

      {/* Types List Modal */}
      <Modal
        open={!!listModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            listModal.close();
            createModal.close();
            editModal.close();
          }
        }}
        title="ຈັດການປະເພດການກວດສອບ"
        description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດການກວດສອບ"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                listModal.close();
                createModal.open();
              }}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ສ້າງປະເພດໃໝ່
            </Button>
          </div>

          {listQuery.isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : listQuery.data?.data.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              ຍັງບໍ່ມີປະເພດ
            </div>
          ) : (
            <div className="space-y-2">
              {listQuery.data?.data.map((type: AuditTypeDTO) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{type.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ລໍາດັບ: {type.orderIndex}
                    </span>
                    {type.description && (
                      <span className="text-muted-foreground text-xs">
                        {type.description}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        listModal.close();
                        editModal.openWith(type);
                      }}
                    >
                      ແກ້ໄຂ
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດ "${type.name}"?`,
                            description: "ປະເພດການກວດສອບນີ້ຈະລຶບອອກຈາກປະເພດທັງຫມົດ",
                            actionText: "ລຶບ",
                            ActionProps: {
                              variant: "destructive",
                            },
                          })
                        ) {
                          await deleteType.mutateAsync(type.id);
                        }
                      }}
                    >
                      ລຶບ
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Type Modal */}
      <Modal
        open={!!createModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            createModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ສ້າງປະເພດ"
        description="ກໍານົດຊື່, ລໍາດັບ, ແລະຄໍາອະທິບາຍຂອງປະເພດ."
        size="sm"
      >
        <AuditTypeForm
          onSubmit={async (vals) => {
            await createType.mutateAsync({
              name: vals.name || "",
              description: vals.description ?? null,
              orderIndex: vals.orderIndex ?? 0,
            });
            createModal.close();
            listModal.open();
          }}
          submitting={createType.isPending}
        />
      </Modal>

      {/* Edit Type Modal */}
      <Modal
        open={!!editModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            editModal.close();
          } else {
            listModal.close();
          }
        }}
        title="ແກ້ໄຂປະເພດ"
        description="ປັບປຸງລາຍລະອຽດຂອງປະເພດ."
        size="sm"
      >
        <AuditTypeForm
          initialValues={
            editModal.data
              ? {
                  name: editModal.data.name,
                  description: editModal.data.description ?? null,
                  orderIndex: editModal.data.orderIndex,
                }
              : undefined
          }
          onSubmit={async (vals) => {
            if (!editModal.data) return;
            await updateType.mutateAsync({
              id: editModal.data.id,
              input: {
                name: vals.name || "",
                description: vals.description ?? null,
                orderIndex: vals.orderIndex ?? 0,
              },
            });
            editModal.close();
            listModal.open();
          }}
          submitting={updateType.isPending}
        />
      </Modal>
    </>
  );
}

export function AuditPlanForm({
  initialData,
  onSubmit,
  isLoading,
  formRef,
}: AuditPlanFormProps) {
  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<AuditPlanFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference issue with optional fields
    resolver: zodResolver(AuditPlanFormSchema) as any,
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      auditTypeId: initialData?.auditTypeId || "",
      sopId: initialData?.sopId || "",
      qcStandardId: initialData?.qcStandardId || "",
      checklistId: initialData?.checklistId || "",
      kpiId: initialData?.kpiId || "",
      departmentId: initialData?.departmentId || "",
      auditorPositionId: initialData?.auditorPositionId || "",
      frequency: initialData?.frequency || "quarterly",
      nextAuditDate: initialData?.nextAuditDate
        ? formatDateForInput(initialData.nextAuditDate)
        : "",
      status: initialData?.status || "pending",
      isActive: initialData?.isActive ?? true,
    },
  });

  const handleAutoFill = () => {
    const sample = auditPlanFormSamples[sampleIndex];
    if (sample) {
      Object.entries(sample).forEach(([key, value]) => {
        // Skip empty strings for required fields - user needs to select these manually
        if (
          (key === "auditTypeId" ||
            key === "departmentId" ||
            key === "auditorPositionId") &&
          value === ""
        ) {
          return;
        }
        methods.setValue(key as keyof AuditPlanFormValues, value as never);
      });
      setSampleIndex((prev) => (prev + 1) % auditPlanFormSamples.length);
      toast.success(
        "Auto-filled form with sample data. Please fill in required fields (ປະເພດການກວດສອບ, ພະແນກ, ຕໍາແໜ່ງຜູ້ກວດສອບ)",
      );
    }
  };

  useEffect(() => {
    if (initialData) {
      methods.reset({
        name: initialData.name,
        description: initialData.description || "",
        auditTypeId: initialData.auditTypeId,
        sopId: initialData.sopId || "",
        qcStandardId: initialData.qcStandardId || "",
        checklistId: initialData.checklistId || "",
        kpiId: initialData.kpiId || "",
        departmentId: initialData.departmentId,
        auditorPositionId: initialData.auditorPositionId,
        frequency: initialData.frequency,
        nextAuditDate: initialData.nextAuditDate
          ? formatDateForInput(initialData.nextAuditDate)
          : "",
        status: initialData.status,
        isActive: initialData.isActive,
      });
    }
  }, [initialData, methods]);

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        getValues: () => methods.getValues(),
        setValue: (name, value) => {
          methods.setValue(name as never, value as never);
        },
      } as AuditPlanFormRef;
    }
  }, [formRef, methods]);

  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });

  const auditTypeId = RHF.useWatch({
    control: methods.control,
    name: "auditTypeId",
  });

  return (
    <FormRoot<AuditPlanFormValues>
      methods={methods}
      onSubmit={(vals) => onSubmit(vals as AuditPlanFormValues)}
      className="space-y-6"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div data-tourid="plan-form-name">
          <FormInput
            name="name"
            label="ຊື່ຜະແນການກວດສອບ"
            placeholder="ຕົວຢ່າງ: ກວດສອບ SOP ການຂາຍ"
            required
          />
        </div>

        <div
          className="flex items-center gap-2"
          data-tourid="plan-form-audit-type"
        >
          <FormInfiniteCombobox<AuditTypeItem>
            name="auditTypeId"
            label="ປະເພດການກວດສອບ"
            requiredMark
            queryKey={["audit-types", "form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/audit-types/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/audit-types/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກປະເພດການກວດສອບ"
            className="flex-1"
          />
          <TypeManagementButton />
        </div>

        <div data-tourid="plan-form-department">
          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ພະແນກ"
            requiredMark
            queryKey={["departments", "form-audit-plan"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ"
          />
        </div>

        <div data-tourid="plan-form-auditor-position">
          <FormInfiniteCombobox<PositionItem>
            name="auditorPositionId"
            label="ຕໍາແໜ່ງຜູ້ກວດສອບ"
            requiredMark
            queryKey={["positions", "form-audit-plan", departmentId]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/positions/lookup?departmentId=${departmentId || ""}`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/positions/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຕໍາແໜ່ງຜູ້ກວດສອບ"
            disabled={!departmentId}
          />
        </div>

        {auditTypeId && (
          <FormInfiniteCombobox<SOPItem>
            name="sopId"
            label="SOP (ຖ້າປະເພດແມ່ນ SOP)"
            queryKey={["sops", "form-audit-plan"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/sops/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/sops/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກ SOP"
            clearable
          />
        )}

        {auditTypeId && (
          <FormInfiniteCombobox<QCStandardItem>
            name="qcStandardId"
            label="ມາດຕະຖານ QC (ຖ້າປະເພດແມ່ນ QC)"
            queryKey={["qc-standards", "form-audit-plan"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/qc-standards/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/qc-standards/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກມາດຕະຖານ QC"
            clearable
          />
        )}

        <FormInfiniteCombobox<ChecklistItem>
          name="checklistId"
          label="Checklist ທີ່ຕ້ອງເຮັດໃນລອບກວດສອບ (ບໍ່ບັງຄັບ)"
          queryKey={["checklists", "form-audit-plan"]}
          queryFn={(args) =>
            fetchLookupForInfinite(
              `${config.apiUrl}/internal-audit/checklists/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(
              `${config.apiUrl}/internal-audit/checklists/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ເລືອກ Checklist"
          clearable
        />

        <FormInfiniteCombobox<KPIItem>
          name="kpiId"
          label="KPI ທີ່ຜົນການກວດສອບຈະນຳໄປອັບເດດ (ບໍ່ບັງຄັບ)"
          queryKey={["kpi", "form-audit-plan"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/kpi/lookup`, args)
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/kpi/lookup`, id)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ເລືອກ KPI"
          clearable
        />

        <div data-tourid="plan-form-frequency">
          <Field name="frequency" label="ຄວາມຖີ່" requiredMark>
            <RHF.Controller
              control={methods.control}
              name="frequency"
              render={({ field }) => (
                <SimpleSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    { value: "monthly", label: "ປະຈຳເດືອນ" },
                    { value: "quarterly", label: "ປະຈຳໄຕມາດ" },
                    { value: "semi_annual", label: "ຄັ້ງຕໍ່ 6 ເດືອນ" },
                    { value: "annual", label: "ປະຈຳປີ" },
                    { value: "ad_hoc", label: "ຕາມຄວາມຈຳເປັນ" },
                  ]}
                  placeholder="ເລືອກຄວາມຖີ່"
                />
              )}
            />
          </Field>
        </div>

        <div data-tourid="plan-form-next-date">
          <FormInput
            name="nextAuditDate"
            label="ວັນທີກວດສອບຄັ້ງຖັດ"
            type="date"
            required
          />
        </div>

        <Field name="status" label="ສະຖານະ" requiredMark>
          <RHF.Controller
            control={methods.control}
            name="status"
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  { value: "pending", label: "ຍັງບໍ່ກວດສອບ" },
                  { value: "in_progress", label: "ກຳລັງກວດສອບ" },
                  { value: "completed", label: "ກວດສອບແລ້ວ" },
                  { value: "cancelled", label: "ຍົກເລີກ" },
                ]}
                placeholder="ເລືອກສະຖານະ"
              />
            )}
          />
        </Field>
      </div>

      <FormTextarea
        name="description"
        label="ລາຍລະອຽດ"
        placeholder="ລາຍລະອຽດເພີ່ມເຕີມກ່ຽວກັບຜະແນການກວດສອບ"
        rows={4}
      />

      <div className="flex justify-between gap-2">
        {isDev && !initialData && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={isLoading}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{auditPlanFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2" data-tourid="plan-form-submit">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
